import { useCallback, useState } from 'react';
import { isAfter } from 'date-fns';
import { useLang } from 'hooks/lang';

import { actions, FullProject } from 'entities';

import { LabeledTextarea, DatePicker, Column, Row, Button } from 'components/Blocks';
import { PropertyWithIcon } from 'components/PropertyWithIcon';
import { ApproveRejectButtons } from 'components/Buttons';

import { ClockIcon, ChatIcon, SendIcon } from 'components/Icons';

import { useProjectUpdate } from 'providers/Project';
import { useToast } from 'providers/Toast';

import { formatDate, getLocalDateString } from 'utils/date';
import { MESSAGES } from './utils';

interface ProjectDateChangeFormProps {
  children?: never;
  project: FullProject;
}

export function ProjectDateChangeForm({ project }: ProjectDateChangeFormProps) {
  const { t } = useLang();
  const { addToast } = useToast();

  const updateProject = useProjectUpdate();

  const [approve, setApprove] = useState(true);

  const [date, setDate] = useState<Date | undefined>(undefined);
  const [justification, setJustification] = useState('');

  const handleApproveClick = useCallback(() => {
    setJustification('');
    setApprove(true);
  }, []);

  const handleRejectClick = useCallback(() => {
    setDate(undefined);
    setApprove(false);
  }, []);

  const { expectedDateRequest } = project;

  const answerRequest = actions.projects.answerProjectDateChange.use({
    block: approve ? !!justification && !!date : !justification,

    params: {
      justification,
      date: approve && date ? getLocalDateString(date as Date) : null,
      approved: approve,
      project: project.id,
    },

    onSuccess: () => {
      updateProject(project.id, { expectedDateRequest: undefined });

      addToast(MESSAGES[approve ? 'changeSuccess' : 'changeRejectSuccess'](t));
    },
  });

  const shouldDisableDate = (date: Date | null): boolean => {
    if (!date) return false;

    return isAfter(new Date(project.expectedDate as string), date);
  };

  return (
    <Column gap="6" fullWidth>
      <Row justifyContent="space-between" className="phone:flex-col">
        <Column>
          <PropertyWithIcon
            icon={<ClockIcon />}
            name={t('current-expected-date')}
            value={formatDate(project?.expectedDate as string)}
          />

          <PropertyWithIcon icon={<ChatIcon />} name={t('user-justification')} value="" />

          <p className="max-w-[560px] rounded-lg border bg-muted p-2">
            {expectedDateRequest!.justification}
          </p>
        </Column>

        {approve ? (
          <Column gap="2" alignItems="center">
            <span className="font-medium">{t('choose-the-new-date')}</span>

            <DatePicker
              onSelect={setDate}
              selected={date}
              disablePast
              disabled={shouldDisableDate}
            />

            <span className="text-sm italic">
              {`${t('suggested-date')}: ${formatDate(expectedDateRequest!.suggestedDate)}`}
            </span>
          </Column>
        ) : (
          <LabeledTextarea
            containerClassName="p-4 rounded-lg border border-dashed"
            value={justification}
            onChange={(e) => setJustification(e.target.value as string)}
            label={t('please-provide-a-justification-for-this-rejection-below')}
          />
        )}
      </Row>

      <Row justifyContent="space-between" className="phone:flex-col">
        <ApproveRejectButtons
          value={approve}
          onChange={approve ? handleRejectClick : handleApproveClick}
        />

        <Button disabled={approve ? !date : !justification} {...answerRequest.buttonProps}>
          <SendIcon />
          {t('send-response')}
        </Button>
      </Row>
    </Column>
  );
}
